import type { ReactNode } from 'react';
import { PageContainer } from '../PageContainer/PageContainer';
import { TemplateSection } from './Section';
import { cx } from '../cx';

export type JobDetailTemplateProps = {
  breadcrumbs?: ReactNode;
  /**
   * The BrandSurface rail: logo, name, Apply and the deadline, flush down the left of the listing
   * card (give it `md:rounded-none`; the card clips the corners) with its content sticky. On phones
   * it is the card's top band with Apply anchored at the top; there is no sticky bottom bar.
   */
  rail: ReactNode;
  /** Freshness and source, above the H1. */
  freshness?: ReactNode;
  title: ReactNode;
  /** Controls beside the title (save, share, match). */
  actions?: ReactNode;
  /** Fact Chips (location, work model, type, level, languages). */
  facts?: ReactNode;
  /** Notices and secondary facts between the header and the pay card. */
  notices?: ReactNode;
  /** The MarketBar panel with a TrendChip. */
  pay?: ReactNode;
  /** "Does it fit you?" ListRows. */
  fit?: { title: ReactNode; content: ReactNode };
  /** The description as Prose; `description` is one line under its title (where the text comes from). */
  description: { title: ReactNode; description?: ReactNode; content: ReactNode };
  /** Further sections below the description, in the article column (skills, requirements, the employer). */
  more?: ReactNode;
  /** The side column: a Readout and similar JobCards. */
  side?: ReactNode;
  /** Extra classes on the listing card (the job page's view-transition hook). */
  cardClassName?: string;
  className?: string;
};

/** The job page: brand rail, facts, pay against the market, fit, description, and related jobs. */
export function JobDetailTemplate({
  breadcrumbs,
  rail,
  freshness,
  title,
  actions,
  facts,
  notices,
  pay,
  fit,
  description,
  more,
  side,
  cardClassName,
  className,
}: JobDetailTemplateProps) {
  return (
    <PageContainer width="wide" className={className}>
      {/* Phones: tighter above and between, so the job starts high on a small screen. */}
      <div className="flex flex-col gap-2 py-3 md:gap-jc-block md:py-jc-block">
        {breadcrumbs}
        <div className="flex flex-col gap-jc-block lg:flex-row lg:items-start lg:gap-jc-section">
          {/* One card (edge to edge on phones, where it is the whole page): the brand rail flush down its left edge (its own content sticky), the listing
              beside it. The rail fills the card's height so the brand colour reads as one panel.
              overflow-clip, not overflow-hidden: hidden makes the card a scroll container, which
              silently turns off the rail's position: sticky. */}
          <div
            className={cx(
              'flex min-w-0 flex-1 flex-col overflow-clip rounded-jc-container border border-jc-card-border bg-jc-surface shadow-jc-sm max-md:-mx-4 max-md:rounded-none max-md:border-x-0 max-md:shadow-none xl:flex-row',
              cardClassName,
            )}
          >
            {/* The rail sits beside the listing only from xl, where the listing still gets room next to the
                similar jobs; below that it is a band on top, so the listing takes the card's full width. */}
            <div className="flex xl:w-52.5 xl:shrink-0 [&>*]:flex-1">{rail}</div>
            {/* Sections of the listing a section apart (the fit, the role, the employer); the header and key facts stay a block apart. */}
            <article className="@container flex min-w-0 flex-1 flex-col gap-jc-section px-5 py-5 md:p-10">
            <div className="flex flex-col gap-jc-block">
            <header className="flex flex-col gap-4">
              {freshness ? <div className="text-sm text-jc-ink-secondary">{freshness}</div> : null}
              {/* Title and actions side by side only when the listing column itself is wide (a container
                  query, not the viewport): beside a rail and a sidebar the buttons used to squeeze the title. */}
              <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-start @2xl:justify-between @2xl:gap-4">
                {/* The working-page title size (type-title-lg), not the landing display size: long titles and Finnish
                    compounds overflowed the card at 52px. Long words hyphenate rather than spill. */}
                <h1 className="type-title-lg min-w-0 break-words hyphens-auto text-jc-ink">{title}</h1>
                {actions ? <div className="flex flex-none items-center">{actions}</div> : null}
              </div>
              {facts ? <div className="flex flex-wrap gap-2">{facts}</div> : null}
            </header>
            {notices}
            {pay}
            </div>
            {fit ? (
              <TemplateSection id="fit" title={fit.title} spacing="lg">
                {fit.content}
              </TemplateSection>
            ) : null}
            <TemplateSection
              id="description"
              title={description.title}
              description={description.description}
              spacing="lg"
              className="border-t border-jc-border pt-jc-section"
            >
              {description.content}
            </TemplateSection>
            {more}
            </article>
          </div>
          {side ? <aside className="flex min-w-0 flex-col gap-jc-block lg:w-80 lg:shrink-0">{side}</aside> : null}
        </div>
      </div>
    </PageContainer>
  );
}
